import { intentForAlert, type AlertIntent } from "./alert-intents.js";
import { alertDestination, formatDestination } from "./destinations.js";
import type { AlertRecord } from "./platform-client.js";
import { outboxPhase } from "./run-state.js";
import { escapeHtml } from "./vault.js";

/**
 * The rows of the alert inbox. Kept apart from `main.ts` so the inbox can be
 * rendered and audited on its own.
 */

function phasePill(headline: string, phase: string): string {
  return `<span class="phase-pill ${phase}">${escapeHtml(headline)}</span>`;
}

/**
 * What an alert is called in the inbox.
 *
 * The server's `title` is the cause-specific sentence and is always preferred.
 * Without one the type is read instead — but as prose, not as the raw slug it
 * is on the wire. Every alert type is a `domain.snake_case_condition` pair (see
 * `server/src/operational-alerts.ts` and its siblings), so the condition alone
 * is already the sentence a reader wants: `operations.job_unhealthy` becomes
 * "Job unhealthy". Deriving it from the slug rather than mapping each type by
 * hand is deliberate — a type registered server-side tomorrow reads correctly
 * here without a client release, which a lookup table could not promise.
 */
export function alertHeadline(alert: Pick<AlertRecord, "title" | "type">): string {
  const title = alert.title?.trim() ?? "";
  if (title !== "") return title;
  const condition = alert.type.split(".").pop()?.replace(/[_-]+/g, " ").trim() ?? "";
  if (condition === "") return "Alert";
  return condition.charAt(0).toUpperCase() + condition.slice(1);
}

/**
 * The count-and-window line, for an alert that has happened more than once.
 *
 * Absent for a single occurrence, deliberately: "1 failure · since 3:16 PM ·
 * latest 3:16 PM" says the same thing three times and is noise on the majority
 * of rows. The line appears exactly when there is something a bare timestamp
 * cannot tell you — how many, and for how long.
 *
 * The noun comes from the server, never from splitting the type key. Every type
 * happens to be `domain.snake_case_condition` today, but that is a convention
 * rather than a rule, and a client inventing "96 unhealthys" from a slug it
 * guessed wrong is worse than a client saying "96 occurrences".
 */
export function occurrenceLine(alert: AlertRecord): string {
  const count = alert.occurrences ?? 1;
  if (count < 2) return "";
  const noun = (alert.occurrenceLabel ?? "occurrence").trim() || "occurrence";
  const since = new Date(alert.createdAt);
  const latest = new Date(alert.lastOccurredAt ?? alert.createdAt);
  const parts = [
    `${count} ${noun}s`,
    `since ${clockTime(since, true)}`,
    // The date is dropped from the latest sighting when it is the same day as
    // the first, which is the common case and the one where repeating it says
    // nothing. A count and a window have to be readable at a glance or they are
    // just more text on a row somebody is trying to triage.
    `latest ${clockTime(latest, !sameDay(since, latest))}`,
  ];
  return `<p class="reader-meta alert-occurrences">${escapeHtml(parts.join(" · "))}</p>`;
}

function sameDay(left: Date, right: Date): boolean {
  return left.toDateString() === right.toDateString();
}

/** A time, with the day in front of it only when the day is in question. */
function clockTime(at: Date, withDate: boolean): string {
  const time = at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (!withDate) return time;
  return `${at.toLocaleDateString([], { month: "numeric", day: "numeric" })} ${time}`;
}

/** A decision still travelling to, or refused by, the hosted vault. */
export function intentRow(intent: AlertIntent): string {
  const copy = outboxPhase(intent.sentAt !== undefined, intent.failure);
  return `<p class="intent-line ${copy.phase}">${phasePill(copy.headline, copy.phase)} ${escapeHtml(intent.action)} · ${escapeHtml(copy.detail)}</p>`;
}

/**
 * One alert with its actions and any pending decision about it.
 *
 * One row of actions per *problem*, which is the whole shape of this inbox. A
 * collapsed alert carries an occurrence count, and Resolve on it settles every
 * occurrence behind it rather than the newest one — see `settle` in
 * `server/src/alert-store.ts`. An inbox that resolved one of ninety-six would
 * look fixed and not be.
 */
export function alertRow(alert: AlertRecord, intents: readonly AlertIntent[]): string {
  const target = alertDestination(alert);
  const pending = intentForAlert(intents, alert.alertId);
  return `<article class="domain-row">
    <button class="domain-open" type="button" data-go="${escapeHtml(formatDestination(target))}">
      <strong>${escapeHtml(alertHeadline(alert))}</strong>
      <small>${escapeHtml(alert.subjectType)} · ${escapeHtml(alert.subjectId)}</small>
      <time>${escapeHtml(new Date(alert.createdAt).toLocaleString())}</time>
    </button>
    ${occurrenceLine(alert)}
    <div class="domain-row-actions">
      <button class="text-button" type="button" data-alert-action="resolve" data-alert="${escapeHtml(alert.alertId)}">Resolve</button>
      <button class="text-button" type="button" data-alert-action="snooze" data-alert="${escapeHtml(alert.alertId)}">Snooze</button>
      <button class="text-button" type="button" data-alert-action="dismiss" data-alert="${escapeHtml(alert.alertId)}">Dismiss</button>
    </div>
    ${pending ? intentRow(pending) : ""}
  </article>`;
}

/**
 * An alert's own content, for an alert whose subject has no page of its own.
 *
 * A notice published by the request ledger is the case this exists for: the
 * whole notice — its title, its body, the line saying where it came from — is
 * carried on the alert, so there is nothing to open and nothing to answer. It
 * is read here and dismissed from the inbox, which is the entire interaction a
 * confirmation or an orphan summary needs.
 */
export function alertContentCard(alert: AlertRecord): string {
  const payload = alert.payload ?? {};
  const body = typeof payload["body"] === "string" ? payload["body"] : "";
  const reason = typeof payload["reason"] === "string" ? payload["reason"] : "";
  return `<div class="domain-empty"><strong>${escapeHtml(alertHeadline(alert))}</strong>`
    + (body === "" ? "" : `<p>${escapeHtml(body)}</p>`)
    + (reason === "" ? "" : `<p class="reader-meta">${escapeHtml(reason)}</p>`)
    + `</div>`
    + `<p class="request-closed" role="status">This is a notice. Dismiss it from the inbox when you have read it.</p>`;
}
